import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  icon?: string
  image?: string
  label?: string
}

export const Service = memo(function Service({ icon, image, label }: Props) {
  const [ref, isVisible] = useInView({ threshold: 0.25, triggerOnce: true })
  const variants = {
    visible: {
      opacity: 1,
      scale: 1,
    },
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
  }

  return (
    <Container
      ref={ref}
      animate={isVisible ? 'visible' : 'hidden'}
      transition={{
        duration: 0.5,
      }}
      variants={variants}
    >
      {image ? (
        <Background>
          <Image style={{ backgroundImage: `url(${image})` }} />
        </Background>
      ) : null}
      <Wrapper>
        {icon ? <SVG src={icon} width="40" height="30" alt={label} /> : null}
        {label ? <Label>{label}</Label> : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled(motion.div)`
  width: calc(25% - 18px);
  margin-bottom: 18px;
  margin-left: 18px;
  background: #54a78b;
  padding-bottom: 25%;
  position: relative;

  @media (max-width: 1200px) {
    width: calc(25% - 15px);
    margin-bottom: 15px;
    margin-left: 15px;
  }

  @media (max-width: 1023px) {
    width: 100%;
    order: 2;
    padding-bottom: 43%;
    &:nth-of-type(3) {
      order: 3;
    }
  }

  @media (max-width: 767px) {
    padding-bottom: 50%;
  }
`

const Background = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark4};
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
  }
`

const Image = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
`

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 1.5rem;
  left: 1.5rem;
  text-align: center;
  transform: translateY(-50%);
  z-index: 2;
`

const SVG = styled.img`
  width: auto;
  height: 72px;
`

const Label = styled.span`
  display: block;
  max-width: 9.375rem;
  margin: 1rem auto 0;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-weight: bold;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  @media (max-width: 1023px) {
    font-size: 12px;
  }
`
